import { camelCase, isEqual as _isEqual, isPlainObject, isString } from 'lodash';
import { isArray, isDefined } from 'class-validator';
import { getLoadtypeShortDirection, ILoadType } from './loadtype';
import { CustomFieldType, type ICustomField } from './custom-field';
import { Maybe, Nullable } from './globals';
import { WarehouseFeatureEnum } from './warehouse';
import type { ISemanticLabel } from './semantic-label';
import { t } from './i18n';
import { extractTextFromHtml } from './js-helpers/str';

/** Prefix for grid tile keys that show Book Load Type custom form fields (`ltcf:<formFieldId>`). */
export const GRID_TILE_LOAD_TYPE_CUSTOM_FORM_FIELD_PREFIX = 'ltcf:' as const;

export function isGridTileLoadTypeCustomFormFieldKey(key: string): boolean {
  return isString(key) && key.startsWith(GRID_TILE_LOAD_TYPE_CUSTOM_FORM_FIELD_PREFIX);
}

export function buildGridTileLoadTypeCustomFormFieldKey(formFieldId: string): string {
  return `${GRID_TILE_LOAD_TYPE_CUSTOM_FORM_FIELD_PREFIX}${formFieldId}`;
}

/** Returns the form field id or null if the key is not a valid load-type custom form grid tile key. */
export function parseGridTileLoadTypeCustomFormFieldId(key: string): string | null {
  if (!isGridTileLoadTypeCustomFormFieldKey(key)) {
    return null;
  }
  const id = key.slice(GRID_TILE_LOAD_TYPE_CUSTOM_FORM_FIELD_PREFIX.length);
  return id.length > 0 ? id : null;
}

export enum CustomFormsFeatures {
  CheckOut = 'check-out',
  // This feature CheckIn is deprecated, use CheckInAdditionalAssetDetails instead
  // TODO: to be removed in the future https://loadsmart.atlassian.net/browse/OD-1534
  // Do not change the string value, as it is used on the database as enum
  CheckIn = 'check-in',
  CheckInAdditionalAssetDetails = 'check-in additional asset details',
  AssetContainerDetails = 'asset container details',
  InboundClaim = 'Inbound Claim',
  OutboundClaim = 'Outbound Claim',
  BookLoadType = 'Book Load Type', // Not installed by default, created by the users
  AppointmentCompleted = 'appointment-completed'
}

export enum ExportableCustomFormsFeatures {
  CheckOut = CustomFormsFeatures.CheckOut,
  CheckInAdditionalAssetDetails = CustomFormsFeatures.CheckInAdditionalAssetDetails,
  InboundClaim = CustomFormsFeatures.InboundClaim,
  OutboundClaim = CustomFormsFeatures.OutboundClaim,
  BookLoadType = CustomFormsFeatures.BookLoadType,
  AssetContainerDetails = CustomFormsFeatures.AssetContainerDetails,
  AppointmentCompleted = CustomFormsFeatures.AppointmentCompleted
}

export const CustomFormsClaimFeatures = [
  CustomFormsFeatures.InboundClaim,
  CustomFormsFeatures.OutboundClaim
];

export const CustomFormsGateManagementFeatures = [
  CustomFormsFeatures.CheckInAdditionalAssetDetails,
  CustomFormsFeatures.CheckOut,
  CustomFormsFeatures.AssetContainerDetails,
  CustomFormsFeatures.AppointmentCompleted
];

// These will get installed right after a warehouse is being created
// Gate Management features are installed by default and controlled by the GM warehouse feature
export const CustomFormsFeaturesInstalledByDefault = [
  ...CustomFormsClaimFeatures,
  ...CustomFormsGateManagementFeatures
];

interface ICustomFormsFeaturesDataItem {
  title: string;
  description?: string;
  helpLink?: string;
  enableDisclaimer?: string;
  disableDisclaimer?: string;
  subtitle?: string;
  showNewTag?: boolean;
}

export interface ITrigger {
  id: string;
  app: AppsEnum;
  category: CustomFormFlowCategoryEnum;
  feature?: CustomFormsFeatures;
  flow?: Maybe<IFlow>;
  flowId?: Maybe<string>;
  objectId: string;
  entityName: CustomFormFlowEntitiesEnum;
  dataEntityName: CustomFormDataEntitiesEnum;
  orgId: string;
}

export interface IFlow {
  id: string;
  name: string;
  orgId: string;
  formFrom: IForm;
  formFromId: string;
  formTo?: Maybe<IForm>;
  formToId?: Maybe<string>;
  description?: string;
  parentCode?: string;
  entityTags?: string[];
  triggers?: ITrigger[];
}

export interface IForm {
  id: string;
  name: string;
  description?: string;
  orgId: string;
  formFields: IFormField[];
}

export interface IFormField {
  id: string;
  required?: boolean;
  overrideLabel?: string;
  overrideDescription?: string;
  field: IField;
}

export interface IField {
  id: string;
  label: string;
  type: CustomFieldType;
  placeholder?: string;
  description?: string;
  orgId: string;
  extraFields?: FieldExtraFields;
  semanticLabel?: ISemanticLabel | null;
}

export interface ICustomFormData {
  id: string;
  name?: string;
  entityName: CustomFormDataEntitiesEnum;
  objectId: string;
  formFieldId: Nullable<string>;
  triggerId: Nullable<string>;
  label: string;
  value: Maybe<string>;
  type: CustomFieldType;
  orgId: string;
}

/** Shape of customformdata rows when mapping to {@link ICustomField} (e.g. email formatting). */
export type CustomFormDataLikeForICustomField = {
  formFieldId?: string | null;
  formField?: { id?: string; fieldId?: string | null } | null;
  label: string;
  type: CustomFieldType;
  value?: ICustomField['value'];
};

/**
 * Maps a persisted custom form row to {@link ICustomField} for display/formatting helpers.
 */
export function mapCustomFormDataLikeToICustomField(
  row: CustomFormDataLikeForICustomField
): ICustomField {
  const name =
    (row.formFieldId && String(row.formFieldId)) ||
    (row.formField?.id && String(row.formField.id)) ||
    (row.formField?.fieldId && String(row.formField.fieldId)) ||
    '';

  let value: ICustomField['value'] = row.value as ICustomField['value'];
  const raw = row.value;
  const needsParsedCollection =
    row.type === CustomFieldType.DropDownMultiSelect || row.type === CustomFieldType.MultiDocument;

  if (isString(raw) && raw.length > 0 && needsParsedCollection) {
    try {
      const parsed = JSON.parse(raw);
      if (isArray(parsed)) {
        value = parsed;
      } else if (!isPlainObject(parsed)) {
        value = parsed as ICustomField['value'];
      }
    } catch {
      value = raw;
    }
  }

  return {
    name,
    label: row.label,
    type: row.type,
    value
  };
}

export const CustomFormsFeaturesData: Record<string, ICustomFormsFeaturesDataItem> = {
  [CustomFormsFeatures.CheckOut]: {
    title: 'Check-Out',
    description: 'Capture check-out data after an appointment is completed.',
    helpLink: ''
  },
  [CustomFormsFeatures.AppointmentCompleted]: {
    title: 'Appointment Completed',
    description: 'Review and manage appointment data after completion.',
    helpLink: ''
  },
  [CustomFormsFeatures.CheckIn]: {
    title: 'Check-in (Deprecated)',
    description:
      'Collect driver info as they self check-in for their planned appointment. This feature is an optional add-on to our Gate Management solution.',
    helpLink:
      '<a href="https://community.loadsmart.com/hc/en-us/articles/19326940613011-Carrier-Self-Check-In" target="_blank">Learn how it works</a> or contact us to <a href="https://lp.opendock.com/gate-management" target="_blank">request a trial.</a>'
  },
  [CustomFormsFeatures.CheckInAdditionalAssetDetails]: {
    title: 'Check-in',
    description: 'Capture check-in details from drivers for both planned and unplanned arrivals.',
    enableDisclaimer: 'The drivers will see this form when they are checking themselves in.',
    disableDisclaimer: 'The drivers will not see this form when they are checking themselves in.',
    subtitle:
      '<i aria-hidden="true" class="v-icon notranslate mr-2 mdi mdi-information-outline" style="font-size: 12px;"></i> <strong>Phone number</strong> and <strong>carrier company</strong> will be collected by default.',
    helpLink:
      '<a href="https://community.loadsmart.com/hc/en-us/articles/19326940613011-Carrier-Self-Check-In" target="_blank">Learn how it works.</a>'
  },
  [CustomFormsFeatures.InboundClaim]: {
    title: 'Received Load Inspection',
    description: 'Document damage, discrepancies or other issues related to the received load.',
    helpLink: ''
  },
  [CustomFormsFeatures.OutboundClaim]: {
    title: 'Shipped Load Verification',
    description: 'Document load integrity and other security measures related to the shipped load.',
    helpLink: ''
  },
  [CustomFormsFeatures.BookLoadType]: {
    title: 'Appointments per Load Type',
    description: 'Capture load type specific details during appointment scheduling.',
    enableDisclaimer:
      'All Fields on this form will be <strong>available and visible</strong> during appointment scheduling.',
    disableDisclaimer:
      'All Fields on this form will be <strong>unavailable</strong> during appointment scheduling.<br />You can re-enable this form at any time.',
    helpLink: ''
  },
  [CustomFormsFeatures.AssetContainerDetails]: {
    title: 'Asset Details',
    description: 'Store and track details of trailers and containers as they arrive at your yard.',
    helpLink: ''
  },
  [WarehouseFeatureEnum.YMS]: {
    title: 'Yard Management System',
    description:
      'Enable the YMS module for this warehouse, also enable the asset visit fields for the gate management app.',
    helpLink: ''
  },
  [WarehouseFeatureEnum.SmartGate]: {
    title: 'SmartGate Integration',
    description: 'Enable the SmartGate module for this warehouse.',
    helpLink: ''
  },
  [WarehouseFeatureEnum.GateManagement]: {
    title: 'Gate Management',
    description:
      'Enable the Gate Management module for this warehouse, which includes the check-in',
    helpLink: ''
  },
  [WarehouseFeatureEnum.ShipperGuideIntegration]: {
    title: 'ShipperGuide Integration',
    description: 'Enable the ShipperGuide Integration module for this warehouse.',
    helpLink: ''
  },
  [WarehouseFeatureEnum.IdValidation]: {
    title: 'ID Validation',
    description: 'Enable the ID Validation for this warehouse',
    helpLink: ''
  }
};

export function getTranslatedCustomFormsFeaturesData() {
  const translated = {} as Record<string, any>;

  for (const [featureKey, raw] of Object.entries(CustomFormsFeaturesData)) {
    const item = raw as ICustomFormsFeaturesDataItem;
    const baseKey = 'core.customForms.customFormsFeaturesData';

    translated[featureKey] = {
      ...item,
      title: t(`${baseKey}.titles.${camelCase(`${item.title}`)}`),
      description: item.description
        ? t(`${baseKey}.descriptions.${camelCase(`${item.description}`)}`)
        : null,
      helpLink: item.helpLink
        ? t(`${baseKey}.helpLinks.${camelCase(extractTextFromHtml(item.helpLink))}`)
        : null,
      enableDisclaimer: item.enableDisclaimer
        ? t(`${baseKey}.enableDisclaimers.${camelCase(extractTextFromHtml(item.enableDisclaimer))}`)
        : null,
      disableDisclaimer: item.disableDisclaimer
        ? t(
            `${baseKey}.disableDisclaimers.${camelCase(
              extractTextFromHtml(item.disableDisclaimer)
            )}`
          )
        : null,
      subtitle: item.subtitle
        ? t(`${baseKey}.subtitles.${camelCase(extractTextFromHtml(item.subtitle))}`)
        : null
    };
  }

  return translated;
}

/*
 Conditions to compare form values to transition forms
 */
export enum TriggerOperatorsEnum {
  greaterThan = '>',
  lessThan = '<',
  greaterThanOrEquals = '>=',
  lessThanOrEquals = '<=',
  equals = '=',
  notEquals = '<>'
}

export function applyFieldOperation(
  a: any,
  b: any,
  operator: TriggerOperatorsEnum,
  isEqual = _isEqual
) {
  a = String(a);
  b = String(b);

  switch (operator) {
    case TriggerOperatorsEnum.equals:
      return isEqual(a, b);
    case TriggerOperatorsEnum.greaterThan:
      return a > b;
    case TriggerOperatorsEnum.lessThan:
      return a < b;
    case TriggerOperatorsEnum.notEquals:
      return !isEqual(String(a), String(b));
    case TriggerOperatorsEnum.greaterThanOrEquals:
      return a >= b;
    case TriggerOperatorsEnum.lessThanOrEquals:
      return a <= b;
    default:
      throw new Error('Unsupported operator');
  }
}

/*
  Target apps for custom forms
 */
export enum AppsEnum {
  Api = 'api',
  Warehouse = 'warehouse',
  Carrier = 'carrier',
  Driver = 'driver',
  All = 'all' // any request source will trigger
}

/*
 Entities that can hold custom forms data, mainly appointment will be used
 */
export enum CustomFormDataEntitiesEnum {
  appointment = 'appointment',
  assetVisit = 'assetvisit',
  assetContainer = 'assetcontainer'
}

// Lazily initialized to avoid circular-dependency issues: CustomFieldType is imported from the
// barrel ('.'), which may not have finished evaluating when this module first loads.
let _customFieldTypeValues: Set<string> | undefined;

/** True when `value` is a known {@link CustomFieldType} enum member. */
export function isCustomFieldType(value: unknown): value is CustomFieldType {
  _customFieldTypeValues ??= new Set<string>(Object.values(CustomFieldType));
  return typeof value === 'string' && _customFieldTypeValues.has(value);
}

function mapCustomFormDataRequestPayloadRowToICustomFormData(
  row: unknown,
  defaults?: { orgId?: string }
): ICustomFormData | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const r = row as Record<string, unknown>;
  const formFieldId = r.formFieldId;
  const label = r.label;
  const type = r.type;
  if (
    !formFieldId ||
    !isString(formFieldId) ||
    !label ||
    !isString(label) ||
    !isDefined(type) ||
    !isCustomFieldType(type)
  ) {
    return null;
  }
  const resolvedOrgId = isString(r.orgId) ? r.orgId : defaults?.orgId;
  if (!resolvedOrgId) {
    return null;
  }
  const triggerIdRaw = r.triggerId;
  const triggerId =
    triggerIdRaw !== null && isDefined(triggerIdRaw) && isString(triggerIdRaw)
      ? triggerIdRaw
      : null;
  return {
    id: isString(r.id) ? r.id : '',
    ...(isString(r.name) ? { name: r.name } : {}),
    entityName: CustomFormDataEntitiesEnum.appointment,
    objectId: isString(r.objectId) ? r.objectId : '',
    formFieldId,
    triggerId,
    label,
    value: (r.value as Maybe<string>) ?? null,
    type,
    orgId: resolvedOrgId
  };
}

/**
 * Maps request `customFormsData` body items to {@link ICustomFormData} for transient appointment payloads.
 * Omits relation objects; `id` / `objectId` may be empty before the appointment exists.
 *
 * Each row must end up with an `orgId`: use `row.orgId` when the client sends it, otherwise
 * `defaults.orgId` (e.g. warehouse org resolved in the controller). Rows missing both are skipped.
 */
export function mapCustomFormsDataRequestPayloadToICustomFormData(
  payload: ReadonlyArray<Record<string, unknown>> | null | undefined,
  defaults?: { orgId?: string }
): ICustomFormData[] {
  if (!isArray(payload) || payload.length === 0) {
    return [];
  }
  const result: ICustomFormData[] = [];
  for (const row of payload) {
    const mapped = mapCustomFormDataRequestPayloadRowToICustomFormData(row, defaults);
    if (mapped) {
      result.push(mapped);
    }
  }
  return result;
}

/*
 Entities that can have flows associated with, default is warehouse
 */
export enum CustomFormFlowEntitiesEnum {
  loadtype = 'loadtype',
  dock = 'dock',
  warehouse = 'warehouse'
}

/*
 * Event names that would trigger custom forms
 */
export enum CustomFormFlowCategoryEnum {
  appointmentStatusCompleted = 'appointmentStatusCompleted',
  appointmentStatusArrived = 'appointmentStatusArrived',
  appointmentCreated = 'appointmentCreated',
  assetVisitCreated = 'assetVisitCreated',
  manualTrigger = 'manualTrigger'
}

export class FieldExtraFields {
  constructor(
    public dropDownValues?: string[],
    public minLengthOrValue?: number,
    public maxLengthOrValue?: number
  ) {}
}

export function getLoadTypesNames(loadTypes: ILoadType[]): string {
  return Array.isArray(loadTypes) && loadTypes.length > 0
    ? [...new Set(loadTypes?.map(l => `${l.name} (${getLoadtypeShortDirection(l)})`).sort())].join(
        '  •  '
      )
    : 'N/A';
}

export function customFormsSwaggerTags(
  tag: 'Forms' | 'Triggers' | 'Form Fields' | 'Flows' | 'Data' | 'Fields'
): {
  tag: string;
  description: string;
} {
  const key = `Custom Forms - ${tag}` as const;
  type DataKeys = typeof key;

  const data: Record<DataKeys, string> = {
    'Custom Forms - Forms':
      'Forms are collections of form fields that represent a complete data entry interface. Each Form belongs to a Flow and captures related data points in a structured way. Forms provide the user interface for data collection.',
    'Custom Forms - Triggers':
      'Triggers are event listeners that activate on specific API or UI events. Each Trigger is associated with exactly one Flow. When a Trigger fires, it initiates its associated Flow, which in turn presents one or two Forms sequentially.',
    'Custom Forms - Form Fields':
      'Form Fields are instances of global Field definitions within a specific Form. While Fields define the data type and general properties, Form Fields customize these instances with form-specific attributes like required status, custom labels, and validation rules.',
    'Custom Forms - Flows':
      'Flows define the sequence and logic of form presentation. Each Flow must have a primary "fromForm" and may optionally include a secondary "formTo" that appears after the first form is completed. Flows connect Triggers to the actual Forms that collect data.',
    'Custom Forms - Data':
      'Data entities store the actual values submitted through Forms. Each form submission creates multiple Data records - one for each Form Field with a value. These records maintain the relationship between the entity (like an appointment), the form field, and the submitted value.',
    'Custom Forms - Fields':
      'Fields are global definitions that specify fundamental attributes like data type (text, number, dropdown, etc.), validation rules, and base properties. Fields are organization-wide resources that can be instantiated as Form Fields across multiple Forms.'
  };

  return { tag: key, description: data[key] || '' };
}
